import { IndexedDbEventStore, EventOutbox, type EventStore, type EventTransport } from "./eventOutbox";
import { productEventTransport } from "./eventTransport";
import { createProductAnalyticsEvent } from "./eventFactory";
import { latencyBucket, NavigationMeasurement } from "./navigationMeasurement";
import { acknowledgeTransitionEnvelope, captureTransitionEnvelope, peekAcquisitionAttribution, peekTransitionEnvelope } from "./transitionEnvelope";
import { productExternalServices } from "../config/externalServices";
import { createProductGa4Reporter } from "./productGa4Reporter";

type RuntimeOptions = {
  artifactSha: string;
  store?: EventStore;
  transport?: EventTransport;
  reportConsentEvent?: (event: string, parameters: Record<string, unknown>) => void;
};

type NavigationEligibility = "eligible" | "redirect" | "access_denied" | "bot" | "synthetic_monitor";

export function createProductAnalyticsRuntime(options: RuntimeOptions) {
  const outbox = new EventOutbox(
    options.store ?? new IndexedDbEventStore(),
    options.transport ?? productEventTransport,
  );
  const pending = new Set<Promise<void>>();
  let verified = false;
  let landingTransition: ReturnType<typeof captureTransitionEnvelope> = null;
  let consentTransitionId: string | null = null;

  const track = (operation: Promise<void>) => {
    pending.add(operation);
    void operation.finally(() => pending.delete(operation)).catch(() => undefined);
  };
  const persistThenDeliver = async (event: Parameters<typeof outbox.enqueue>[0]) => {
    await outbox.enqueue(event);
    await outbox.flush();
  };
  const navigation = new NavigationMeasurement(
    (event) => track(persistThenDeliver(event)),
    { artifactSha: options.artifactSha },
  );

  return {
    async start(url = new URL(window.location.href)) {
      landingTransition = captureTransitionEnvelope(url) ?? peekTransitionEnvelope();
      consentTransitionId = landingTransition?.transitionId ?? null;
    },
    async authVerified() {
      verified = true;
      const attribution = peekAcquisitionAttribution(options.artifactSha);
      if (attribution) {
        await outbox.enqueue(attribution);
        acknowledgeTransitionEnvelope(attribution.transitionId);
      }
      await outbox.flush();
    },
    async flush() {
      if (verified) await outbox.flush();
    },
    async loginCompleted(loginAttemptId: string, methodCategory: "password" | "google" | "auth0_sso" | "other_provider", durationMs: number) {
      await persistThenDeliver(createProductAnalyticsEvent("login_completed", {
        loginAttemptId, methodCategory, latencyBucket: latencyBucket(Math.max(0, durationMs)),
      }, { artifactSha: options.artifactSha }));
    },
    reportLogin(event: "login_started" | "login_failed", fields: {
      loginAttemptId: string;
      methodCategory: "password" | "google" | "auth0_sso" | "other_provider";
      reasonCategory?: string;
    }) {
      if (!consentTransitionId || !options.reportConsentEvent) return false;
      options.reportConsentEvent(event, {
        event, clientEventId: crypto.randomUUID(), occurredAt: new Date().toISOString(),
        host: "app", artifactSha: options.artifactSha, consentState: "granted",
        transitionId: consentTransitionId, ...fields,
      });
      return true;
    },
    navigationStarted(routeTemplate: string, eligibilityCategory: NavigationEligibility = "eligible") {
      return navigation.started(routeTemplate, eligibilityCategory);
    },
    navigationReady(navigationId: string, routeTemplate: string) {
      return navigation.ready(navigationId, routeTemplate);
    },
    landingReady(_routeTemplate: string) {
      if (landingTransition && options.reportConsentEvent) {
        const navigationId = crypto.randomUUID();
        options.reportConsentEvent("product_landing", {
          event: "product_landing",
          clientEventId: crypto.randomUUID(),
          occurredAt: new Date().toISOString(),
          host: "app",
          artifactSha: options.artifactSha,
          navigationId,
          landingPath: landingTransition.landingPath,
          referrerClass: Object.keys(landingTransition.utm).length ? "campaign" : "internal",
          consentState: "granted",
          transitionId: landingTransition.transitionId,
        });
        landingTransition = null;
        return navigationId;
      }
      return null;
    },
    navigationFailed(navigationId: string, routeTemplate: string) {
      return navigation.failed(navigationId, routeTemplate, "route_load");
    },
    async settled() {
      while (pending.size) await Promise.allSettled([...pending]);
    },
  };
}

const artifactSha = import.meta.env.VITE_ARTIFACT_SHA ?? "";
const productGa4Reporter = createProductGa4Reporter({
  measurementId: productExternalServices.gaMeasurementId ?? "",
});
const productConsentReporter = (event: string, parameters: Record<string, unknown>) => {
  window.dispatchEvent(new CustomEvent("pinpoint:product-analytics", { detail: { event, parameters } }));
  productGa4Reporter(event, parameters);
};
const singleton = typeof window !== "undefined" && typeof indexedDB !== "undefined" && /^[0-9a-f]{64}$/.test(artifactSha)
  ? createProductAnalyticsRuntime({
    artifactSha,
    reportConsentEvent: productConsentReporter,
  })
  : null;
let activeNavigationId: string | null = null;

export function classifyProductNavigationEligibility(navigatorLike: Pick<Navigator, "webdriver" | "userAgent"> = navigator) {
  if (navigatorLike.webdriver) return "synthetic_monitor" as const;
  if (/(?:bot|crawler|spider|headless)/i.test(navigatorLike.userAgent)) return "bot" as const;
  return "eligible" as const;
}

export function resolveProductNavigationEligibility(
  routeEligibility: Extract<NavigationEligibility, "eligible" | "redirect" | "access_denied"> = "eligible",
  navigatorLike: Pick<Navigator, "webdriver" | "userAgent"> = navigator,
): NavigationEligibility {
  const browserEligibility = classifyProductNavigationEligibility(navigatorLike);
  return browserEligibility === "eligible" ? routeEligibility : browserEligibility;
}

export async function startProductAnalytics() {
  await singleton?.start();
}

export async function recordVerifiedAttribution() {
  await singleton?.authVerified();
}

export async function flushProductAnalytics() {
  await singleton?.flush();
}

export async function recordProductLoginCompleted(loginAttemptId: string, methodCategory: "password" | "google" | "auth0_sso" | "other_provider", durationMs: number) {
  await singleton?.loginCompleted(loginAttemptId, methodCategory, durationMs);
}

export function recordConsentedProductLoginStarted(loginAttemptId: string, methodCategory: "password" | "google" | "auth0_sso" | "other_provider") {
  return singleton?.reportLogin("login_started", { loginAttemptId, methodCategory }) ?? false;
}

export function recordConsentedProductLoginFailed(loginAttemptId: string, methodCategory: "password" | "google" | "auth0_sso" | "other_provider", reasonCategory: string) {
  return singleton?.reportLogin("login_failed", { loginAttemptId, methodCategory, reasonCategory }) ?? false;
}

export function startProductNavigation(
  routeTemplate: string,
  routeEligibility: Extract<NavigationEligibility, "eligible" | "redirect" | "access_denied"> = "eligible",
) {
  activeNavigationId = singleton?.navigationStarted(
    routeTemplate,
    resolveProductNavigationEligibility(routeEligibility),
  ) ?? null;
  return activeNavigationId;
}

export function markProductNavigationReady(navigationId: string | null, routeTemplate: string) {
  const marked = navigationId ? singleton?.navigationReady(navigationId, routeTemplate) ?? false : false;
  if (marked && activeNavigationId === navigationId) activeNavigationId = null;
  return marked;
}

export function recordProductLandingReady(routeTemplate: string) {
  return singleton?.landingReady(routeTemplate) ?? null;
}

export function failProductNavigation(navigationId: string | null, routeTemplate: string) {
  const failed = navigationId ? singleton?.navigationFailed(navigationId, routeTemplate) ?? false : false;
  if (failed && activeNavigationId === navigationId) activeNavigationId = null;
  return failed;
}

export function failCurrentProductNavigation(routeTemplate: string) {
  return failProductNavigation(activeNavigationId, routeTemplate);
}
